import styled from '@emotion/styled'
import React, { useContext, useMemo } from 'react'
import { CartContext } from '../../../context/cartContext'
import {
    CustomizationContext,
    PRIMARY_CTA_FILL_COLOR_VAR,
    PRIMARY_PAGE_FILL_COLOR_VAR,
    PRIMARY_PAGE_TEXT_COLOR_VAR,
    SUBTLE_BORDER_COLOR_VAR,
    SUBTLE_PRIMARY_COLOR_VAR,
} from '../../../context/CustomizationContextProvider'
import { ViddyLPContext } from '../../../context/viddyLPContext'
import { getTypographyRoleStyle } from '../../../style/typography'
import {
    priceFormat,
    formatPriceWithoutCents,
} from '../../../utils/priceFormat'
import { CheckmarkIcon } from '../../icons/Icons'
import { scaledFontSize } from '../../../style/scrollingLayout'

const Container = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: `color-mix(in srgb, var(${SUBTLE_BORDER_COLOR_VAR}) 30%, var(${PRIMARY_PAGE_FILL_COLOR_VAR}) 70%)`,
    borderBottom: `1px solid var(${SUBTLE_BORDER_COLOR_VAR})`,
})

const ProgressText = styled.div({
    ...getTypographyRoleStyle('promoText', {
        fontWeight: 400,
    }),
    fontSize: scaledFontSize(14),
    color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
    marginBottom: 8,
    textAlign: 'center',
})

const ThresholdReachedText = styled.span({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
})

const ProgressBarRow = styled.div({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gap: 8,
})

const Track = styled.div({
    flex: 1,
    height: 8,
    backgroundColor: `var(${SUBTLE_BORDER_COLOR_VAR})`,
    borderRadius: 4,
    overflow: 'hidden',
})

const Fill = styled.div<{ percentage: number }>(({ percentage }) => ({
    height: '100%',
    width: `${percentage}%`,
    backgroundColor: `var(${PRIMARY_CTA_FILL_COLOR_VAR})`,
    borderRadius: 4,
    transition: 'width 0.3s ease',
}))

const ThresholdLabel = styled.span({
    ...getTypographyRoleStyle('captionText', {
        fontWeight: 400,
    }),
    fontSize: scaledFontSize(13),
    color: `var(${SUBTLE_PRIMARY_COLOR_VAR})`,
    whiteSpace: 'nowrap',
})

export const FreeShippingProgressBar: React.FC = () => {
    const { freeShippingEnabled, freeShippingMinCartAmount } =
        useContext(CustomizationContext)
    const { getCartTotal, cart } = useContext(CartContext)
    const { landingPage } = useContext(ViddyLPContext)

    const cartTotal = getCartTotal()

    const {
        isVisible,
        hasReachedThreshold,
        percentage,
        deltaFormatted,
        thresholdFormatted,
    } = useMemo(() => {
        // Don't display if cart is loading, empty, or total is zero
        if (
            cart.isLoading ||
            cart.items.length === 0 ||
            cartTotal === 0 ||
            !freeShippingEnabled ||
            freeShippingMinCartAmount <= 0
        ) {
            return {
                isVisible: false,
                hasReachedThreshold: false,
                percentage: 0,
                deltaFormatted: '',
                thresholdFormatted: '',
            }
        }

        const reached = cartTotal >= freeShippingMinCartAmount
        const pct = Math.min((cartTotal / freeShippingMinCartAmount) * 100, 100)
        const delta = freeShippingMinCartAmount - cartTotal
        const deltaFmt = priceFormat(delta, landingPage.client?.currencySymbol)
        // Format threshold without cents
        const thresholdFmt = formatPriceWithoutCents(
            freeShippingMinCartAmount,
            landingPage.client?.currencySymbol,
        )

        return {
            isVisible: true,
            hasReachedThreshold: reached,
            percentage: pct,
            deltaFormatted: deltaFmt,
            thresholdFormatted: thresholdFmt,
        }
    }, [
        freeShippingEnabled,
        freeShippingMinCartAmount,
        cartTotal,
        cart.isLoading,
        cart.items.length,
        landingPage.client?.currencySymbol,
    ])

    if (!isVisible) {
        return null
    }

    return (
        <Container>
            <ProgressText>
                {hasReachedThreshold ? (
                    <ThresholdReachedText>
                        <CheckmarkIcon
                            width={14}
                            height={14}
                            color={`var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`}
                        />
                        You&apos;ve earned
                        <strong> FREE SHIPPING!</strong>
                    </ThresholdReachedText>
                ) : (
                    <>
                        Add {deltaFormatted} for
                        <strong> FREE SHIPPING!</strong>
                    </>
                )}
            </ProgressText>
            <ProgressBarRow>
                <Track>
                    <Fill percentage={percentage} />
                </Track>
                {!hasReachedThreshold && (
                    <ThresholdLabel>{thresholdFormatted}</ThresholdLabel>
                )}
            </ProgressBarRow>
        </Container>
    )
}
